import type { TFunction } from 'i18next';

import type { FilaItem } from '@/domain/fila/interfaces/fila';

/**
 * The three questions the home offers, built with the DAY's own data so the person taps
 * instead of typing. Each one falls back to a generic phrasing when the data it needs did
 * not arrive: a suggestion naming nothing is still a good way in, a suggestion naming
 * "undefined" is a defect.
 */
export interface ICopilotoSugestao {
  id: string;
  texto: string;
}

/** Dates are `YYYY-MM-DD`, so the smallest string is the oldest day. */
function maisAntiga(atual: FilaItem | null, candidata: FilaItem): FilaItem {
  const entregaAtual = atual?.entrega ?? '';
  const entregaCandidata = candidata.entrega ?? '';
  if (entregaAtual === '') {
    return candidata;
  }
  if (entregaCandidata === '') {
    return atual ?? candidata;
  }
  return entregaCandidata < entregaAtual ? candidata : (atual ?? candidata);
}

/**
 * The most delayed OP: the overdue one whose delivery date is the oldest. Nothing overdue
 * means nothing to name, and the question becomes the generic one.
 *
 * Read from `vencida` and never from the reason: being overdue is urgency, not a reason,
 * so an OP that is both late and out of fabric is named by the fabric and would never be
 * found by a reason filter.
 */
function opMaisAtrasada(itens: Array<FilaItem>): string {
  const vencidas = itens.filter((item) => item.vencida && !item.resolvido);
  if (vencidas.length === 0) {
    return '';
  }
  return vencidas.reduce<FilaItem | null>(maisAntiga, null)?.opRaw ?? '';
}

/**
 * A cell whose team came short today: it is the queue item that reports exactly that, and
 * the only place the home already knows a cell name with an attendance problem.
 */
function celulaComFalta(itens: Array<FilaItem>): string {
  const item = itens.find((candidato) => candidato.tipo === 'EQUIPE_REDUZIDA' && !candidato.resolvido);
  return (item?.celula ?? '').trim();
}

export function buildCopilotoSugestoes(t: TFunction, itens: Array<FilaItem>): Array<ICopilotoSugestao> {
  const op = opMaisAtrasada(itens);
  const celula = celulaComFalta(itens);
  return [
    {
      id: 'atraso',
      texto: op === '' ? t('home.copiloto.sugestao.atrasoGenerica') : t('home.copiloto.sugestao.atraso', { op }),
    },
    { id: 'travou', texto: t('home.copiloto.sugestao.travou') },
    {
      id: 'presenca',
      texto:
        celula === '' ? t('home.copiloto.sugestao.presencaGenerica') : t('home.copiloto.sugestao.presenca', { celula }),
    },
  ];
}
